import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import {
  createSpotQuoteInProgress,
  createSpotQuoteReset,
  getCurrentQuoteCollaboratorsList,
  resetCopyQuoteKey,
  resetCurrentQuoteCarrier,
  resetCurrentQuoteCollaborators,
  resetCurrentQuoteRoutingGuidesParams,
  resetQuoteBidExpirationDate,
  setCurrentQuoteRoutingGuidesParams,
  setQuoteActiveAccordions,
  setQuoteModalBlocker,
} from '@redux/common/CommonActions.js';
import { resetCreateShipmentQuote, resetShipmentRateShopList } from '@redux/shipment/ShipmentActions.js';
import { getQuotePayloadProductTotals } from '@views/screens/Quote/components/CreateGetQuote/Tabs/ProductDetails/utils';
import { useGetQuoteModuleSettings } from '@views/screens/Quote/hooks/index.js';
import { hasRoutingParamsChanged, isRoutingParamsComplete, parseRoutingParams } from '@views/screens/Quote/utils';
import { Button, Col, Form, Row, Spin } from 'antd';
import { DoubleRightOutlined } from '@ant-design/icons';
import { routeFileSelector } from '@utilz/i18n.js';

import { ENV_VARS } from '@/env';
import { APP_ROUTES } from '@/contants/routes.js';
import {
  FormSchema,
  FormsRegistryProvider,
  useRegisterForm,
  useValidationSchema,
  VALIDATION_SCHEMA_KEYS,
} from '@/modules/validators';
import { parseCreateSpotQuoteDataFromFormData } from '@/utilz/index.js';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import { ReactComponent as SaveIcon } from '../../../assets/icons/save.svg';
import { QuoteCargoTypeSelect } from './../../../modules/spot-quote/components/quote-cargo-types-select';
import IconButton from '../../components/Buttons/IconButton';
import SvgConverter from '../../components/Common/SvgConverter';
import PrimaryLabel from '../../components/Labels/PrimaryLabel';
import SecondaryLabel from '../../components/Labels/SecondaryLabel';
import Paper from '../../components/Paper/Paper';
import { formsName } from '../Shipment/components/QuoteCard/constants';
import HallCreateGetQuoteTabs from './components/CreateGetQuote/HallCreateGetQuoteTabs';
import { SECTIONS_BY_FORMS, TAB_KEYS } from './constants';

const isHalliburton = ENV_VARS.REACT_APP_THEME === 'halliburton';

// todo GetQuoteScreen for HB
const HallGetQuoteScreen = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { quote: prepopulatedData, encodedRatesKey, cargoType } = location.state || {};
  const [cargoTypeForm] = Form.useForm();
  useRegisterForm(cargoTypeForm, 'cargoTypeForm');

  const {
    createSpotQuote,
    quoteModalBlocker,
    currentSpotQuoteNoAndTitle,
    activeAccordion,
    appLoader,
    currentSpotRoutingGuidesParams,
    copyQuoteKey,
  } = useSelector((state) => state.common);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const numberOfTabKeys = Object.keys(TAB_KEYS).length;

  const { data: quoteModuleSettingsData } = useGetQuoteModuleSettings();
  const { isRoutingGuideEnabled, isCargoTypeEnabled, settingsData } = quoteModuleSettingsData || {};

  const { data: schema } = useValidationSchema(VALIDATION_SCHEMA_KEYS.spotQuote.create);
  const isCollaboratorsEnabled = schema?.validationSchema?.properties?.collaborators;
  const prevRoutingGuideParamsRef = useRef();

  const setActiveAccordion = useCallback(
    (value) => {
      const accordions = [];
      if (!Array.isArray(value)) {
        accordions.push(value);
      }
      dispatch(setQuoteActiveAccordions([...accordions, ...value]));
    },
    [dispatch],
  );

  useEffect(() => {
    if (prepopulatedData?.cargoTypeForUI || cargoType) {
      cargoTypeForm.setFieldsValue({
        cargoType: prepopulatedData?.cargoTypeForUI || cargoType,
      });
    }
  }, [prepopulatedData, cargoType, cargoTypeForm]);

  useEffect(() => {
    dispatch(resetShipmentRateShopList());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetCreateShipmentQuote());
      dispatch(resetCurrentQuoteRoutingGuidesParams());
      dispatch(resetCurrentQuoteCarrier());
      dispatch(resetCurrentQuoteCollaborators());
      dispatch(resetQuoteBidExpirationDate());
    };
  }, [dispatch]);

  const validateForm = (formInstance, formName) => {
    return formInstance
      .validateFields()
      .then((validationStatus) => {
        return { [formName]: validationStatus };
      })
      .catch((error) => {
        throw { formName, ...error };
      });
  };

  const handleSubmit = async (_triggeredForm, { forms }) => {
    try {
      setIsSubmitting(true);

      const formList = formsName.reduce((acc, formName) => {
        if (forms[formName]) {
          acc.push(validateForm(forms[formName], formName));
        }

        return acc;
      }, []);

      const validateResponse = await globalThis.Promise.all(formList);

      const formResponse = validateResponse.reduce((a, b) => {
        // biome-ignore lint/performance/noAccumulatingSpread: <need to check>
        return { ...a, ...b };
      }, {});

      const isHaveUnsavedFiles = formResponse.documentForm.documents.find((item) => item.isNew && !item?.file);
      if (isHaveUnsavedFiles) {
        showNotification({
          message: 'Please complete or cancel adding the file',
          type: NOTIFICATION_TYPES.ERROR,
        });

        return;
      }

      const footerFields = getQuotePayloadProductTotals(formResponse.productForm.quoteDetails);

      const createSpotQuoteData = parseCreateSpotQuoteDataFromFormData({
        ...footerFields,
        ...formResponse.quoteDetailsForm,
        ...formResponse.shippingTermsSearchForm,
        ...formResponse.notesForm,
        ...formResponse.documentForm,
        ...formResponse?.cargoTypeForm,
        distance: formResponse?.stopsForm?.consigneeAdditionalInformations[0].distance,
        ports: formResponse?.stopsForm?.ports,
        products: formResponse?.productForm?.quoteDetails,
        transportationMode: formResponse?.stopsForm?.transportationMode,
      });
      const documentsPayload = formResponse?.documentForm?.documents?.map((value) => value.formData);

      dispatch(
        createSpotQuoteInProgress({
          encodedRatesKey,
          isCollaboratorsEnabled,
          quote: createSpotQuoteData,
          quoteDocuments: documentsPayload,
        }),
      );
    } catch (e) {
      setActiveAccordion([SECTIONS_BY_FORMS[e.formName]]);
    } finally {
      dispatch(setQuoteModalBlocker(false));
      setIsSubmitting(false);
    }
  };

  // redirect if the quote is successfully created
  useEffect(() => {
    if (createSpotQuote.success && currentSpotQuoteNoAndTitle.data.quoteNo && !appLoader) {
      dispatch(createSpotQuoteReset());
      dispatch(resetCopyQuoteKey());
      setActiveAccordion([]);
      navigate(
        generatePath(APP_ROUTES.SEARCH_QUOTE_ID, {
          quoteKey: currentSpotQuoteNoAndTitle.data.quoteNo?.slice(5),
        }),
      );
    }
  }, [
    createSpotQuote.success,
    currentSpotQuoteNoAndTitle.data.quoteNo,
    dispatch,
    navigate,
    appLoader,
    setActiveAccordion,
  ]);

  const handleFormChanges = (forms) => {
    if (!isRoutingGuideEnabled || !settingsData?.IsRoutingGuideEnabled) {
      return;
    }

    try {
      const routingParams = parseRoutingParams(settingsData, forms);

      dispatch(setCurrentQuoteRoutingGuidesParams(routingParams));
    } catch (e) {
      console.error('Dynamic Routing Error:', e);
    }
  };

  useEffect(() => {
    if (!isRoutingGuideEnabled || !settingsData?.IsRoutingGuideEnabled) {
      return;
    }

    try {
      const requiredKeys = JSON.parse(settingsData.IsRoutingGuideEnabled);
      const prev = prevRoutingGuideParamsRef.current;

      const shouldFetch =
        !copyQuoteKey &&
        isRoutingParamsComplete(requiredKeys, currentSpotRoutingGuidesParams) &&
        hasRoutingParamsChanged(requiredKeys, prev, currentSpotRoutingGuidesParams);

      if (shouldFetch) {
        dispatch(getCurrentQuoteCollaboratorsList());
      }

      prevRoutingGuideParamsRef.current = { ...(currentSpotRoutingGuidesParams || {}) };
    } catch (e) {
      console.error('Routing guide settings parse error:', e);
    }
  }, [dispatch, isRoutingGuideEnabled, settingsData, currentSpotRoutingGuidesParams, copyQuoteKey]);

  const handleClickExpandCollapse = () => {
    if (activeAccordion.length !== numberOfTabKeys) {
      setActiveAccordion(Object.values(TAB_KEYS));
    } else {
      setActiveAccordion([]);
    }
  };

  return schema ? (
    <FormsRegistryProvider>
      <FormSchema schema={schema.validationSchema}>
        <Form.Provider
          onFormFinish={handleSubmit}
          onFormChange={(_, { forms }) => {
            handleFormChanges(forms);
            if (!quoteModalBlocker) {
              dispatch(setQuoteModalBlocker(true));
            }
          }}
        >
          <Row className="flex-direction-column flex-nowrap h-100">
            <Col className="mb-15" data-testid="get-quote-screen">
              <Paper className="p-15">
                <Row align="middle" justify="space-between" gutter={30}>
                  <Col>
                    <PrimaryLabel className="text-black">Create Quote</PrimaryLabel>
                  </Col>
                  <Col>
                    <Row gutter={30} align="middle">
                      <Col>
                        <Row gutter={0} align="middle">
                          {isCargoTypeEnabled && (
                            <Form name="cargoTypeForm" form={cargoTypeForm}>
                              <Col className="lh-0 mr-18">
                                <FormSchema.Field name="cargoType">
                                  {({ name, antdRule, isRequired, label }) => (
                                    <QuoteCargoTypeSelect
                                      editMode
                                      name={name}
                                      label={label}
                                      isRequired={isRequired}
                                      disabled={prepopulatedData}
                                      rules={[antdRule]}
                                    />
                                  )}
                                </FormSchema.Field>
                              </Col>
                            </Form>
                          )}
                          <Form name="saveQuoteForm" className="lh-0">
                            <IconButton buttonType="submit" loading={isSubmitting}>
                              <Row gutter={10} align="middle">
                                <Col className="lh-0">
                                  <SvgConverter
                                    Icon={SaveIcon}
                                    width={20}
                                    height={20}
                                    color={`header ${isHalliburton ? 'hb-icon' : ''}`}
                                  />
                                </Col>
                                <Col className="lh-0">
                                  <SecondaryLabel className="f-14 f-400">
                                    {t('translation:default.buttons.save')}
                                  </SecondaryLabel>
                                </Col>
                              </Row>
                            </IconButton>
                          </Form>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Paper>
            </Col>
            <Col>
              <Row className="mb-15">
                <Button
                  icon={<DoubleRightOutlined rotate={activeAccordion.length === numberOfTabKeys ? 90 : 0} />}
                  type="text"
                  onClick={handleClickExpandCollapse}
                >
                  {activeAccordion.length === numberOfTabKeys ? 'Collapse All' : 'Expand All'}
                </Button>
              </Row>
            </Col>
            <Col className="flex-1 custom-scroll-bar">
              <HallCreateGetQuoteTabs activeKey={activeAccordion} setActiveKey={setActiveAccordion} />
            </Col>
          </Row>
        </Form.Provider>
      </FormSchema>
    </FormsRegistryProvider>
  ) : (
    <Spin size="large" className="w-full flex-1 d-flex justify-content-center align-items-center" />
  );
};

export default memo(HallGetQuoteScreen);
